import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Attendance brochure — the first reference build of the Phase 6 template.
 *
 * Tone: "we did the math so you don't have to". Pitch is calibrated to the
 * HR/Admin reader who closes payroll on Tuesdays and is tired of chasing
 * "I forgot to clock out" emails.
 *
 * The illustration is a small synthetic "phone with a clock-in button" SVG
 * built inline — geofence ring, a faint map grid, three floating timestamp
 * pills. No external assets, no stock images. Pure SVG so it scales cleanly
 * for dark-mode work later.
 *
 * The mockup is a team-week table: 5 employees × 5 weekdays, status pills
 * (Present/Late/Off), plus a small "today" sidebar. Tailwind classes mirror
 * the real app's table tokens so the screenshot reads as real product UI.
 */

// ----- Illustration: phone with clock-in button, geofence, timestamp pills -----

const AttendanceIllustration: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Faint background map grid + geofence — communicates "on-site detection". */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        {/* Subtle map grid */}
        <defs>
          <pattern id="att-grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path
              d="M 24 0 L 0 0 0 24"
              fill="none"
              stroke="rgb(99 102 241 / 0.10)"
              strokeWidth="1"
            />
          </pattern>
          <radialGradient id="att-geofence" cx="50%" cy="55%" r="48%">
            <stop offset="60%" stopColor="rgb(99 102 241 / 0.0)" />
            <stop offset="100%" stopColor="rgb(99 102 241 / 0.18)" />
          </radialGradient>
        </defs>
        <rect width="320" height="400" fill="url(#att-grid)" />
        <circle cx="160" cy="220" r="150" fill="url(#att-geofence)" />
        <circle
          cx="160"
          cy="220"
          r="118"
          fill="none"
          stroke="rgb(99 102 241 / 0.35)"
          strokeWidth="1.5"
          strokeDasharray="4 6"
        />
      </svg>

      {/* Phone — drawn as nested divs so we can compose Tailwind on it. */}
      <div className="absolute left-1/2 top-1/2 h-[80%] w-[58%] -translate-x-1/2 -translate-y-1/2 rounded-[28px] border border-slate-200 bg-white p-3 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)]">
        <div className="h-full rounded-[20px] bg-gradient-to-b from-indigo-50 via-white to-white p-4">
          {/* Status bar */}
          <div className="flex items-center justify-between text-[9px] font-semibold text-slate-400">
            <span className="tabular-nums">8:42</span>
            <span className="flex items-center gap-1">
              <span className="h-1 w-3 rounded-sm bg-slate-300" />
              <span className="h-1 w-1 rounded-full bg-slate-300" />
            </span>
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
            {t("brochure.attendance.illustration.today")}
          </p>
          <p className="mt-1 text-xs font-semibold text-slate-900">
            {t("brochure.attendance.illustration.dateExample")}
          </p>

          {/* Clock-in CTA */}
          <button
            type="button"
            disabled
            className="mt-5 flex h-20 w-20 cursor-default flex-col items-center justify-center gap-1 rounded-full bg-indigo-600 text-white shadow-[0_12px_30px_-8px_rgba(79,70,229,0.55)] mx-auto"
            aria-hidden="true"
          >
            <Icon name="Fingerprint" size={22} />
            <span className="text-[9px] font-semibold uppercase tracking-wider">
              {t("brochure.attendance.illustration.tapToClockIn")}
            </span>
          </button>

          <div className="mt-4 rounded-lg bg-white px-2.5 py-2 ring-1 ring-slate-200">
            <p className="text-[9px] text-slate-500">
              {t("brochure.attendance.illustration.location")}
            </p>
            <p className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-slate-900">
              <Icon name="MapPin" size={10} className="text-indigo-600" />
              {t("brochure.attendance.illustration.locationName")}
            </p>
          </div>
        </div>
      </div>

      {/* Floating timestamp pills — three of them, one labeled "now" highlighted. */}
      <div className="absolute left-[6%] top-[14%] flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600 shadow-[0_6px_16px_-6px_rgba(15,23,42,0.25)] ring-1 ring-slate-200">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="tabular-nums">8:42</span>
      </div>
      <div className="absolute right-[4%] top-[28%] flex items-center gap-1.5 rounded-full bg-indigo-600 px-2.5 py-1 text-[10px] font-semibold text-white shadow-[0_8px_18px_-6px_rgba(79,70,229,0.55)]">
        <span className="h-1.5 w-1.5 rounded-full bg-white" />
        <span className="tabular-nums">{t("brochure.attendance.illustration.justNow")}</span>
      </div>
      <div className="absolute right-[10%] bottom-[10%] flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600 shadow-[0_6px_16px_-6px_rgba(15,23,42,0.25)] ring-1 ring-slate-200">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span className="tabular-nums">17:08</span>
      </div>
    </div>
  );
};

// ----- Mockup: faux attendance dashboard (week × team grid) -----

type DayCellStatus = "present" | "late" | "off" | "absent";

interface DayCell {
  status: DayCellStatus;
  in?: string;
  out?: string;
}

interface RowData {
  initials: string;
  name: string;
  nameKey: string;
  role: string;
  roleKey: string;
  tint: string;
  days: DayCell[];
}

const ROWS: RowData[] = [
  {
    initials: "RA",
    name: "Reem A.",
    nameKey: "brochure.attendance.mockup.person.reem",
    role: "Reception",
    roleKey: "brochure.attendance.mockup.role.reception",
    tint: "bg-indigo-100 text-indigo-700",
    days: [
      { status: "present", in: "08:42", out: "17:05" },
      { status: "present", in: "08:38", out: "17:10" },
      { status: "late", in: "09:12", out: "17:14" },
      { status: "present", in: "08:40", out: "17:02" },
      { status: "present", in: "08:34", out: "16:58" },
    ],
  },
  {
    initials: "HK",
    name: "Hassan K.",
    nameKey: "brochure.attendance.mockup.person.hassan",
    role: "Field Tech",
    roleKey: "brochure.attendance.mockup.role.fieldTech",
    tint: "bg-emerald-100 text-emerald-700",
    days: [
      { status: "present", in: "07:30", out: "16:35" },
      { status: "present", in: "07:28", out: "16:40" },
      { status: "present", in: "07:31", out: "16:45" },
      { status: "off" },
      { status: "present", in: "07:25", out: "16:32" },
    ],
  },
  {
    initials: "SM",
    name: "Sara M.",
    nameKey: "brochure.attendance.mockup.person.sara",
    role: "Accounts",
    roleKey: "brochure.attendance.mockup.role.accounts",
    tint: "bg-amber-100 text-amber-800",
    days: [
      { status: "present", in: "09:01", out: "18:02" },
      { status: "late", in: "09:24", out: "18:10" },
      { status: "present", in: "08:55", out: "17:58" },
      { status: "present", in: "09:00", out: "18:03" },
      { status: "absent" },
    ],
  },
  {
    initials: "OF",
    name: "Omar F.",
    nameKey: "brochure.attendance.mockup.person.omar",
    role: "Warehouse",
    roleKey: "brochure.attendance.mockup.role.warehouse",
    tint: "bg-sky-100 text-sky-700",
    days: [
      { status: "present", in: "06:55", out: "15:30" },
      { status: "present", in: "06:58", out: "15:32" },
      { status: "present", in: "06:54", out: "15:28" },
      { status: "present", in: "07:02", out: "15:35" },
      { status: "present", in: "06:50", out: "15:30" },
    ],
  },
  {
    initials: "LT",
    name: "Lina T.",
    nameKey: "brochure.attendance.mockup.person.lina",
    role: "Support",
    roleKey: "brochure.attendance.mockup.role.support",
    tint: "bg-violet-100 text-violet-700",
    days: [
      { status: "off" },
      { status: "present", in: "09:30", out: "18:30" },
      { status: "present", in: "09:32", out: "18:28" },
      { status: "late", in: "09:55", out: "18:35" },
      { status: "present", in: "09:30", out: "18:30" },
    ],
  },
];

const STATUS_STYLES: Record<DayCellStatus, { dot: string; text: string; bg: string }> = {
  present: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
  late: { dot: "bg-amber-500", text: "text-amber-800", bg: "bg-amber-50" },
  off: { dot: "bg-slate-300", text: "text-slate-500", bg: "bg-slate-50" },
  absent: { dot: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50" },
};

const AttendanceMockup: React.FC = () => {
  const { t } = useTranslation();
  const dayKeys = [
    "brochure.attendance.mockup.day.mon",
    "brochure.attendance.mockup.day.tue",
    "brochure.attendance.mockup.day.wed",
    "brochure.attendance.mockup.day.thu",
    "brochure.attendance.mockup.day.fri",
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* Team-week table */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="CalendarDays" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.attendance.mockup.weekHeading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400 tabular-nums">
            {t("brochure.attendance.mockup.weekRange")}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-white">
                <th className="w-44 px-3 py-2 text-start text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t("brochure.attendance.mockup.member")}
                </th>
                {dayKeys.map((k) => (
                  <th
                    key={k}
                    className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400"
                  >
                    {t(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ROWS.map((r) => (
                <tr key={r.initials} className="bg-white">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={cn(
                          "inline-flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold",
                          r.tint,
                        )}
                      >
                        {r.initials}
                      </span>
                      <div className="leading-tight">
                        <p className="text-xs font-semibold text-slate-900">{t(r.nameKey)}</p>
                        <p className="text-[10px] text-slate-500">{t(r.roleKey)}</p>
                      </div>
                    </div>
                  </td>
                  {r.days.map((d, i) => {
                    const s = STATUS_STYLES[d.status];
                    return (
                      <td key={i} className="px-2 py-2.5 text-center">
                        <div
                          className={cn(
                            "mx-auto inline-flex items-center gap-1 rounded-md px-2 py-1",
                            s.bg,
                          )}
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
                          <span
                            className={cn(
                              "text-[10px] font-semibold tabular-nums",
                              s.text,
                            )}
                          >
                            {d.status === "off"
                              ? t("brochure.attendance.mockup.status.off")
                              : d.status === "absent"
                                ? t("brochure.attendance.mockup.status.absent")
                                : d.in}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* "Today" sidebar — present/late/off counts + a tiny clock card. */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.attendance.mockup.todayHeading")}
          </p>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("brochure.attendance.mockup.present")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">42</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t("brochure.attendance.mockup.late")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">3</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                {t("brochure.attendance.mockup.onLeave")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">5</span>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
            {t("brochure.attendance.mockup.exceptions")}
          </p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-indigo-900">2</p>
          <p className="mt-0.5 text-[10px] leading-snug text-indigo-800/80">
            {t("brochure.attendance.mockup.exceptionsHint")}
          </p>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const AttendanceBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "MapPin",
      title: t("brochure.attendance.features.geofence.title"),
      description: t("brochure.attendance.features.geofence.description"),
    },
    {
      icon: "FileSpreadsheet",
      title: t("brochure.attendance.features.timesheets.title"),
      description: t("brochure.attendance.features.timesheets.description"),
    },
    {
      icon: "PenSquare",
      title: t("brochure.attendance.features.corrections.title"),
      description: t("brochure.attendance.features.corrections.description"),
    },
    {
      icon: "Users",
      title: t("brochure.attendance.features.availability.title"),
      description: t("brochure.attendance.features.availability.description"),
    },
    {
      icon: "Map",
      title: t("brochure.attendance.features.maps.title"),
      description: t("brochure.attendance.features.maps.description"),
    },
    {
      icon: "Download",
      title: t("brochure.attendance.features.reports.title"),
      description: t("brochure.attendance.features.reports.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: "67%",
      label: t("brochure.attendance.metrics.timeSavings"),
    },
    {
      value: t("brochure.attendance.metrics.onboardValue"),
      label: t("brochure.attendance.metrics.onboardLabel"),
    },
    {
      value: t("brochure.attendance.metrics.zeroValue"),
      label: t("brochure.attendance.metrics.zeroLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.attendance.useCases.hr.persona"),
      scenario: t("brochure.attendance.useCases.hr.scenario"),
      outcome: t("brochure.attendance.useCases.hr.outcome"),
    },
    {
      persona: t("brochure.attendance.useCases.supervisor.persona"),
      scenario: t("brochure.attendance.useCases.supervisor.scenario"),
      outcome: t("brochure.attendance.useCases.supervisor.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="attendance"
      hero={{
        eyebrow: t("brochure.attendance.hero.eyebrow"),
        headline: t("brochure.attendance.hero.headline"),
        subheadline: t("brochure.attendance.hero.subheadline"),
        illustration: <AttendanceIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.attendance.mockup.caption"),
        content: <AttendanceMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default AttendanceBrochure;
